import React, { useState } from 'react';
import styled from '@emotion/styled';
import { sortSizes } from '../firebase/schema';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 12px;
  width: 650px;
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  color: var(--al-primary);
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: rgba(255, 255, 255, 0.6);
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const LineInfo = styled.div`
  padding: 16px 24px;
  background: rgba(var(--al-primary-rgb), 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const LineName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
`;

const LineMeta = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 16px;
  border-radius: 10px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Montserrat', sans-serif;
  
  &.complete {
    background: rgba(var(--al-primary-rgb), 0.15);
    border-color: rgba(var(--al-primary-rgb), 0.3);
    color: var(--al-primary);
    
    &:hover {
      background: rgba(var(--al-primary-rgb), 0.25);
      border-color: var(--al-primary);
      transform: translateY(-2px);
    }
  }
  
  &.reduce {
    background: rgba(255, 183, 77, 0.15);
    border-color: rgba(255, 183, 77, 0.3);
    color: #FFB74D;
    
    &:hover {
      background: rgba(255, 183, 77, 0.25);
      border-color: #FFB74D;
      transform: translateY(-2px);
    }
  }
  
  &.reprint {
    background: rgba(255, 112, 67, 0.15);
    border-color: rgba(255, 112, 67, 0.3);
    color: #FF7043;
    
    &:hover {
      background: rgba(255, 112, 67, 0.25);
      border-color: #FF7043;
      transform: translateY(-2px);
    }
  }
  
  svg {
    font-size: 2rem;
  }
`;

const ActionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SizeSection = styled.div`
  margin-top: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  color: white;
  font-size: 0.85rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const SizeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SizeCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 65px;
  flex: 1;
  max-width: 85px;
`;

const SizeLabel = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const SizeInput = styled.input`
  width: 100%;
  padding: 10px 8px;
  text-align: center;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s;
  
  /* Remove spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.mode === 'reduce' ? '#FFB74D' : '#FF7043'};
    background: rgba(0, 0, 0, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }
`;

const OriginalQty = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 4px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 20px;
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: ${props.mode === 'reduce' ? '#FFB74D' : '#FF7043'};
    border: none;
    color: #1a1a1a;
    
    &:hover {
      background: ${props.mode === 'reduce' ? '#FFA726' : '#F4511E'};
    }
  ` : `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

const TotalSummary = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  
  span.value {
    color: ${props => props.mode === 'reduce' ? '#FFB74D' : '#FF7043'};
    font-weight: 700;
    font-size: 1rem;
  }
`;

const QCPrintedModal = ({ line, onClose, onComplete }) => {
  const [mode, setMode] = useState(null); // null, 'reduce', or 'reprint'
  const [adjustedSizes, setAdjustedSizes] = useState({});

  const handleSizeChange = (size, value) => {
    const newValue = parseInt(value) || 0;
    setAdjustedSizes(prev => ({ ...prev, [size]: newValue }));
  };

  const handleComplete = () => {
    onComplete(line.id, 'PRINTED');
  };

  const handleReduceQuantities = () => {
    if (mode !== 'reduce') {
      setMode('reduce');
      setAdjustedSizes({});
      return;
    }

    // Calculate new total quantities - ensure all values are numbers
    const newSizes = {};
    Object.entries(line.sizes).forEach(([size, qty]) => {
      newSizes[size] = Number(qty) || 0;
    });
    
    // Apply reductions
    Object.entries(adjustedSizes).forEach(([size, reduction]) => {
      if (reduction > 0) {
        newSizes[size] = Math.max(0, (newSizes[size] || 0) - reduction);
      }
    });

    onComplete(line.id, 'REDUCED', {
      originalLine: {
        sizes: newSizes,
        totalQuantity: Object.values(newSizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
      }
    });
  };

  const handleCreateReprint = () => {
    if (mode !== 'reprint') {
      setMode('reprint');
      setAdjustedSizes({});
      return;
    }

    // Create reprint line with specified quantities - ensure all values are numbers
    const reprintQuantities = {};
    Object.entries(adjustedSizes).forEach(([size, qty]) => {
      reprintQuantities[size] = Number(qty) || 0;
    });
    
    const totalReprintQuantity = Object.values(reprintQuantities)
      .reduce((sum, qty) => sum + (Number(qty) || 0), 0);

    if (totalReprintQuantity === 0) {
      alert('Please enter quantities to reprint');
      return;
    }

    // Ensure original line sizes are also numbers
    const originalSizes = {};
    Object.entries(line.sizes).forEach(([size, qty]) => {
      originalSizes[size] = Number(qty) || 0;
    });

    onComplete(line.id, 'PARTIALLY_PRINTED', {
      originalLine: {
        sizes: originalSizes,
        totalQuantity: Object.values(originalSizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
      },
      reprintLine: {
        sizes: reprintQuantities,
        totalQuantity: totalReprintQuantity
      }
    });
  };

  // Calculate total for entered quantities
  const enteredTotal = Object.values(adjustedSizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);

  // Get sorted sizes that have quantities
  const activeSizes = Object.keys(line.sizes)
    .filter(size => Number(line.sizes[size]) > 0)
    .sort((a, b) => sortSizes(a, b));

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Quality Control</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon fontSize="small" />
          </CloseButton>
        </ModalHeader>

        <LineInfo>
          <LineName>{line.designName || 'Unnamed Design'}</LineName>
          <LineMeta>
            {line.garmentId} • {line.garmentColor} • {line.totalQuantity || Object.values(line.sizes).reduce((s, q) => s + (Number(q) || 0), 0)} pcs
          </LineMeta>
        </LineInfo>

        <ModalBody>
          {!mode && (
            <ActionGrid>
              <ActionButton className="complete" onClick={handleComplete}>
                <CheckCircleIcon />
                <ActionLabel>Complete</ActionLabel>
              </ActionButton>
              <ActionButton className="reduce" onClick={handleReduceQuantities}>
                <RemoveCircleIcon />
                <ActionLabel>Reduce Qty</ActionLabel>
              </ActionButton>
              <ActionButton className="reprint" onClick={handleCreateReprint}>
                <ReplayIcon />
                <ActionLabel>Reprint</ActionLabel>
              </ActionButton>
            </ActionGrid>
          )}

          {(mode === 'reduce' || mode === 'reprint') && (
            <SizeSection>
              <SectionHeader>
                <SectionTitle>
                  {mode === 'reduce' ? 'Enter quantity to reduce' : 'Enter quantity to reprint'}
                </SectionTitle>
              </SectionHeader>
              
              <SizeRow>
                {activeSizes.map(size => (
                  <SizeCell key={size}>
                    <SizeLabel>{size}</SizeLabel>
                    <SizeInput
                      type="number"
                      min="0"
                      max={line.sizes[size]}
                      placeholder="0"
                      value={adjustedSizes[size] || ''}
                      onChange={(e) => handleSizeChange(size, e.target.value)}
                      mode={mode}
                    />
                    <OriginalQty>of {line.sizes[size]}</OriginalQty>
                  </SizeCell>
                ))}
              </SizeRow>

              <TotalSummary mode={mode}>
                {mode === 'reduce' ? 'Reducing:' : 'Reprinting:'} <span className="value">{enteredTotal}</span> pieces
              </TotalSummary>

              <ButtonRow>
                <Button onClick={() => setMode(null)}>
                  Back
                </Button>
                <Button 
                  primary 
                  mode={mode}
                  onClick={mode === 'reduce' ? handleReduceQuantities : handleCreateReprint}
                >
                  {mode === 'reduce' ? 'Confirm Reduction' : 'Create Reprint'}
                </Button>
              </ButtonRow>
            </SizeSection>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QCPrintedModal;
